import { useId } from "react";

import { muiGlyph } from "@/components/ui/mui-glyph";
import { cn } from "@/lib/utils";

// The two glyphs the donor's status chips lead with.
//
// The catalog drew lucide's `Clock` and `CircleAlert` in their place — stroked
// outlines at 12px, where the donor's are filled shapes at the small chip's
// 18px icon slot — so one status looked different depending on whose icon set
// had drawn it.
//
// Sized by the slot they sit in (`StaticChip`'s icon slot is 18px), so each is
// declared at MUI's 24px box and the chip resizes it, exactly as MUI's
// `.MuiChip-iconSmall` does to an `SvgIcon`.

/**
 * `@mui/icons-material/AccessTime.js`: MUI ships it as two paths — the ring and
 * the hands — joined here into one. They fill the same under `nonzero`: the
 * ring's inner edge winds against its outer, so its interior is at zero and the
 * hands fill inside it either way.
 */
const ACCESS_TIME_PATH =
  "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8 M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z";

/**
 * The pending chip's clock. Its ink is the chip's to set — the donor forces
 * `#E38D0D` with `!important` on both of its pending chips
 * (`item-status-chip.tsx`, `item-pending-label.tsx`).
 */
const AccessTimeGlyph = muiGlyph("mui-access-time", ACCESS_TIME_PATH, "size-6");

export function MuiAccessTimeIcon({ className }: { className?: string }) {
  return <AccessTimeGlyph className={className} />;
}

/**
 * The donor's own Not Recommended mark — `src/images/icons/not-recommended-icon
 * .tsx`, not an MUI glyph: a `#C72525` disc under a white diamond with an
 * exclamation, clipped by a mask to the disc's inner 16px. Copied byte for
 * byte, in its own 18×18 box.
 *
 * The mask id is per instance: a card grid draws twenty of these, and an `id`
 * repeated in one document is the first one's for every `url(#…)`.
 */
export function NotRecommendedIcon({ className }: { className?: string }) {
  const maskId = useId();

  return (
    <svg
      data-slot="not-recommended-icon"
      aria-hidden="true"
      focusable="false"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      className={cn("size-[18px] shrink-0", className)}
    >
      <rect width="18" height="18" rx="9" fill="#C72525" />
      <mask id={maskId} maskUnits="userSpaceOnUse" x="1" y="1" width="16" height="16">
        <rect x="1" y="1" width="16" height="16" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path
          d="M9 15.6833C8.82222 15.6833 8.65278 15.65 8.49167 15.5833C8.33056 15.5167 8.18333 15.4222 8.05 15.3L2.7 9.95001C2.57778 9.81668 2.48333 9.66946 2.41667 9.50835C2.35 9.34724 2.31667 9.17779 2.31667 9.00001C2.31667 8.82224 2.35 8.65002 2.41667 8.48335C2.48333 8.31668 2.57778 8.17224 2.7 8.05001L8.05 2.70001C8.18333 2.56668 8.33056 2.46946 8.49167 2.40835C8.65278 2.34724 8.82222 2.31668 9 2.31668C9.17778 2.31668 9.35 2.34724 9.51667 2.40835C9.68333 2.46946 9.82778 2.56668 9.95 2.70001L15.3 8.05001C15.4333 8.17224 15.5306 8.31668 15.5917 8.48335C15.6528 8.65002 15.6833 8.82224 15.6833 9.00001C15.6833 9.17779 15.6528 9.34724 15.5917 9.50835C15.5306 9.66946 15.4333 9.81668 15.3 9.95001L9.95 15.3C9.82778 15.4222 9.68333 15.5167 9.51667 15.5833C9.35 15.65 9.17778 15.6833 9 15.6833ZM9 14.35L14.35 9.00001L9 3.65001L3.65 9.00001L9 14.35ZM8.33333 9.66668H9.66667V5.66668H8.33333V9.66668ZM9 11.6667C9.18889 11.6667 9.34722 11.6028 9.475 11.475C9.60278 11.3472 9.66667 11.1889 9.66667 11C9.66667 10.8111 9.60278 10.6528 9.475 10.525C9.34722 10.3972 9.18889 10.3333 9 10.3333C8.81111 10.3333 8.65278 10.3972 8.525 10.525C8.39722 10.6528 8.33333 10.8111 8.33333 11C8.33333 11.1889 8.39722 11.3472 8.525 11.475C8.65278 11.6028 8.81111 11.6667 9 11.6667Z"
          fill="white"
        />
      </g>
    </svg>
  );
}

// The Integrations card's three status glyphs (#747) — the donor's `StatusChip`
// imports the **filled** `CheckCircle`, `Error` and `Help`
// (`ConsumedApis.tsx:16-25`), not their `*Outlined` siblings. Paths copied from
// the installed `@mui/icons-material@9.2.0` in the pinned modsy checkout. Drawn
// at MUI's 24px box and resized by the chip's 18px icon slot, as the two
// glyphs above are.

const CheckCircleGlyph = muiGlyph(
  "mui-check-circle",
  "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z",
  "size-6",
);

const ErrorGlyph = muiGlyph(
  "mui-error",
  "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m1 15h-2v-2h2zm0-4h-2V7h2z",
  "size-6",
);

const HelpGlyph = muiGlyph(
  "mui-help",
  "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m1 17h-2v-2h2zm2.07-7.75-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25",
  "size-6",
);

/** `@mui/icons-material/CheckCircle.js` — the Operational chip's mark. */
export function MuiCheckCircleIcon({ className }: { className?: string }) {
  return <CheckCircleGlyph className={className} />;
}

/** `@mui/icons-material/Error.js` — the Down chip's mark. */
export function MuiErrorIcon({ className }: { className?: string }) {
  return <ErrorGlyph className={className} />;
}

/** `@mui/icons-material/Help.js` — the Unknown chip's mark. */
export function MuiHelpIcon({ className }: { className?: string }) {
  return <HelpGlyph className={className} />;
}
